import React, { useContext, useEffect, useState } from "react";
import { Box, Button } from "@xometry/xometry_loft";
import fileUtils, { FormDataFile } from "../../../../../../utils/fileUtils";
import {
  trackClick,
  TrackingEvent,
} from "../../../../../../utils/trackingUtils";
import ModelViewerContext from "../../../../contexts/modelViewerContext";
import { useConfig } from "../../../../contexts/configContext";

type LineItemThumbnailProps = {
  file: FormDataFile;
} & React.ComponentProps<typeof Box>;

export const LineItemThumbnail = ({
  file,
  ...props
}: LineItemThumbnailProps): React.ReactElement | null => {
  const [fileId, setFileId] = useState<string>();
  const [thumbnailUrl, setThumbnailUrl] = useState<string>();
  const [thumbnailError, setThumbnailError] = useState(false);
  const { openModelViewer } = useContext(ModelViewerContext);
  const { origins } = useConfig();

  useEffect(() => {
    async function tryGetThumbnailUrl() {
      const url = await fileUtils.getFileThumbnailUrl(
        origins.partFilesService,
        file.id
      );

      if (url) {
        setThumbnailUrl(url);
      }
    }

    if (
      file.id !== fileId &&
      fileUtils.shouldHaveThumbnailImage(file.originalFilename)
    ) {
      setFileId(file.id);
      setThumbnailUrl(undefined);
      setThumbnailError(false);
      fileUtils.callWithRetry(tryGetThumbnailUrl).catch(() => {
        setThumbnailError(true);
      });
    }
  }, [file]);

  if (thumbnailError) {
    return null;
  }

  if (!fileUtils.shouldHaveThumbnailImage(file.originalFilename)) {
    return null;
  }

  const handleClick: React.MouseEventHandler<any> = event => {
    event.stopPropagation();

    if (!fileUtils.shouldHaveStreamcacheFile(file.originalFilename)) {
      return;
    }

    trackClick(TrackingEvent.LINE_ITEM_THUMBNAIL_CLICKED, { fileId: file.id });

    if (openModelViewer) {
      openModelViewer(fileUtils.toModelViewerFile(file));
    }
  };

  let content;

  if (thumbnailUrl) {
    content = (
      <Button link onClick={handleClick}>
        <img
          src={thumbnailUrl}
          alt={file.originalFilename}
          width="60px"
          height="60px"
        />
      </Button>
    );
  }

  return <Box {...props}>{content}</Box>;
};

export default LineItemThumbnail;
